import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CloseIcon } from '../../assets/icons/modal/close.svg';
import { ReactComponent as FireIcon } from '../../assets/icons/wiz/fire.svg';
import { ReactComponent as ThunderIcon } from '../../assets/icons/wiz/thunder.svg';
import { BottomSheet } from '../../horizon-components/bottom-sheet/ui';
import { MButton } from '../../horizon-components/MButton';
import { Modal } from '../../horizon-components/Modal';
import { useWizContext } from '../../providers/WizProvider/WizProvider';
import { QUIZ_COUNT } from '../../shared/const/wiz';
import { QuizStartModal } from '../QuizStartModal';
import { QuizTestModal } from '../QuizTestModal';
import './style.scss';

interface QuizHeaderProps {
  className?: string;
}

export const QuizHeader = ({ className }: QuizHeaderProps) => {
  const { t } = useTranslation(['translation']);
  const [isStreakModalOpen, setIsStreakModalOpen] = useState(false);
  const [isEnergyModalOpen, setIsEnergyModalOpen] = useState(false);
  const {
    livesCount,
    nextLifeRecoveryTime,
    currentDay,
    currentMultiplier,
    fetchLivesData,
    fetchForceCompleteQuiz,
    currentTest,
    questions,
  } = useWizContext();

  const targetDate = nextLifeRecoveryTime
    ? new Date(nextLifeRecoveryTime).getTime()
    : null;

  const [timeLeft, setTimeLeft] = useState(
    targetDate ? targetDate - new Date().getTime() : 0
  );

  useEffect(() => {
    if (!targetDate) return;

    const timerId = setInterval(() => {
      const now = new Date().getTime();
      const difference = targetDate - now;
      if (difference <= 0) {
        clearInterval(timerId);
        setTimeLeft(0);
        fetchLivesData();
      } else {
        setTimeLeft(difference);
      }
    }, 1000);

    return () => clearInterval(timerId);
  }, [targetDate]);

  useEffect(() => {
    if (nextLifeRecoveryTime) {
      const newTargetDate = new Date(nextLifeRecoveryTime).getTime();
      setTimeLeft(newTargetDate - new Date().getTime());
    }
  }, [nextLifeRecoveryTime]);

  const formatTime = (milliseconds: number) => {
    if (milliseconds <= 0) return '0:00:00';

    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const count = questions?.length ? QUIZ_COUNT + 1 - questions.length : QUIZ_COUNT;

  return (
    <div className={classNames('QuizHeader', className)}>
      <div className="QuizHeader__item">
        {!currentTest ? (
          <MButton
            variant="highlighted"
            color="primary"
            size="xs"
            className="!rounded-full"
            onClick={() => setIsStreakModalOpen(true)}
          >
            {currentDay} <span className="sm-max:hidden">{t('wiz.days')}</span>{' '}
            <FireIcon />
          </MButton>
        ) : questions ? (
          <span
            className={'ml-1.5 text-base font-semibold text-purple-600 lg:text-xl'}
          >
            {t('wiz.Question')} {count}/{QUIZ_COUNT}
          </span>
        ) : null}
      </div>
      <div className="QuizHeader__item justify-end">
        {!currentTest ? (
          <>
            <span>{livesCount < 5 && timeLeft > 0 && formatTime(timeLeft)}</span>
            <MButton
              variant="secondary"
              color="primary"
              size="xs"
              className="!rounded-full"
              onClick={() => setIsEnergyModalOpen(true)}
            >
              {livesCount} <ThunderIcon />
            </MButton>
          </>
        ) : (
          <MButton
            variant="secondary"
            color="primary"
            size="xs"
            className="!h-9 !w-9 !rounded-full !p-2"
            onClick={fetchForceCompleteQuiz}
          >
            <CloseIcon />
          </MButton>
        )}
      </div>
      {window.innerWidth > 768 ? (
        <Modal
          isOpen={isStreakModalOpen}
          onClose={() => setIsStreakModalOpen(false)}
          showClose={false}
        >
          <QuizStartModal
            currentDayNumber={currentDay}
            multiplier={currentMultiplier}
            onStart={() => setIsStreakModalOpen(false)}
          />
        </Modal>
      ) : (
        <BottomSheet
          open={isStreakModalOpen}
          onDismiss={() => setIsStreakModalOpen(false)}
        >
          <QuizStartModal
            currentDayNumber={currentDay}
            multiplier={currentMultiplier}
            onStart={() => setIsStreakModalOpen(false)}
          />
        </BottomSheet>
      )}
      {window.innerWidth > 768 ? (
        <Modal
          isOpen={isEnergyModalOpen}
          onClose={() => setIsEnergyModalOpen(false)}
          showClose={false}
        >
          <QuizTestModal onClose={() => setIsEnergyModalOpen(false)} />
        </Modal>
      ) : (
        <BottomSheet
          open={isEnergyModalOpen}
          onDismiss={() => setIsEnergyModalOpen(false)}
        >
          <QuizTestModal onClose={() => setIsEnergyModalOpen(false)} />
        </BottomSheet>
      )}
    </div>
  );
};
